import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import Footer from "../components/Footer";
import { useLanguage } from "../context/LanguageContext";
import LanguageSwitch from "../components/LanguageSwitch";

const Cocons2025Bali = () => {
  const { language } = useLanguage();
  const [isPostCoconExpanded, setIsPostCoconExpanded] = useState(false);
  const [isPreCoconExpanded, setIsPreCoconExpanded] = useState(false);

  // Translation helper
  const t = (fr, en) => language === 'en' ? en : fr;

  // SEO meta tags - optimisés sans répétition
  useEffect(() => {
    document.title = language === 'en' 
      ? "COCON Sacré Bali 2025 | First Ubud Edition – Initiatory women's retreat"
      : "COCON Sacré Bali 2025 | Première édition Ubud – Retraite initiatique femmes";
    
    let metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute("content", language === 'en'
        ? "First COCON Sacré edition in Bali, March 2025. 8-day initiatory crossing in Ubud: Balinese rituals, deep circles, nature immersion. Foundation of the SWIM · PRAY · RADIATE arc."
        : "Première édition COCON Sacré à Bali, mars 2025. Traversée initiatique de 8 jours à Ubud : rituels balinais, cercles profonds, immersion nature. Fondation de l'arc NAGE · PRIE · RAYONNE."
      );
    }
  }, [language]);

  return (
    <div className="min-h-screen bg-[#F5EDE4] text-[#5C4A3D]">
      <LanguageSwitch />
      
      {/* Header */}
      <header className="px-6 pt-8 pb-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <a
            href="/cocon-sacre-2026"
            className="text-sm text-[#8B7B6B] hover:text-[#5C4A3D] transition-colors"
          >
            {t("← Retour à l'accueil", "← Back to home")}
          </a>
          <p className="text-xs tracking-[0.25em] uppercase text-[#8B7B6B]">
            {t("Bali · Mars 2025", "Bali · March 2025")}
          </p>
        </div>
      </header>

      {/* Hero - Logo COCON Sacré */}
      <section className="px-6 py-6">
        <div className="max-w-sm mx-auto">
          <img
            src="https://customer-assets.emergentagent.com/job_a9e4b3eb-6cf0-46b7-85a6-000614fbaf25/artifacts/huv5p05t_ccon%20sacre%CC%81%20logo.png"
            alt={t("COCON Sacré – Première édition Bali 2025", "COCON Sacré – First Bali Edition 2025")}
            className="w-full h-auto object-contain"
          />
        </div>
      </section>

      {/* Titre principal + Introduction */}
      <section className="px-6 py-8">
        <div className="max-w-3xl mx-auto text-center">
          <h1 className="text-lg md:text-xl tracking-[0.2em] uppercase font-medium text-[#5C4A3D] mb-4">
            {t("BALI · MARS 2025", "BALI · MARCH 2025")}
          </h1>
          <p className="text-xs tracking-[0.3em] uppercase text-[#8B7B6B] mb-8">
            {t("Retraite COCON Sacré · Bali 2025", "COCON Sacré Retreat · Bali 2025")}
          </p>

          {/* Introduction - Ton mature et structuré */}
          <div className="space-y-6 text-base leading-relaxed text-[#6B5A4D]">
            <p>
              {t(
                <>En mars 2025, <span className="font-medium uppercase">COCON Sacré</span> a réuni un cercle intime de femmes au cœur d'Ubud et du nord de Bali.</>,
                <>In March 2025, <span className="font-medium uppercase">COCON Sacré</span> gathered an intimate circle of women in the heart of Ubud and in the north of Bali.</>
              )}
            </p>
            <p>
              {t(
                <>Pendant <span className="font-medium">8 jours et 7 nuits</span>, nous avons vécu une traversée initiatique mêlant pratiques incarnées, rituels balinais, immersion dans la nature et cercles profonds.</>,
                <>For <span className="font-medium">8 days and 7 nights</span>, we lived an initiatory journey weaving together embodied practices, Balinese rituals, immersion in nature, and deep sharing circles.</>
              )}
            </p>
            <p className="text-[#5C4A3D]">
              {t(
                <>Cette première édition à Bali a posé les fondations de ce qui deviendra, en 2026,<br />l'arc complet <span className="font-medium tracking-wide">NAGE · PRIE · RAYONNE</span>.</>,
                <>This first Bali edition laid the foundations for what will become, in 2026,<br />the full arc <span className="font-medium tracking-wide">SWIM · PRAY · RADIATE</span>.</>
              )}
            </p>
            
            {/* Bloc distinctif */}
            <div className="border-l-2 border-[#8B7B6B]/40 pl-5 py-2 my-6 text-left">
              <p className="text-sm text-[#5C4A3D] italic">
                {t(
                  <>Elle était plus resserrée.<br />Plus concentrée.<br />Déjà exigeante.</>,
                  <>It was more intimate.<br />More concentrated.<br />Already deeply demanding.</>
                )}
              </p>
            </div>
            
            <p className="text-sm text-[#8B7B6B]">
              {t(
                "Cette page en retrace les temps forts : souvenirs, images, témoignages et accompagnement avant et après l'expérience.",
                "This page retraces the highlights: memories, images, testimonials, and the guidance offered before and after the experience."
              )}
            </p>
          </div>
        </div>
      </section>

      {/* Séparateur */}
      <div className="w-16 h-px bg-[#8B7B6B]/30 mx-auto my-4"></div>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* BROCHURE */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <section className="py-12 px-6 bg-[#E8DED4]">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-sm tracking-[0.3em] uppercase font-medium text-[#5C4A3D] mb-6">
            {t("Brochure · Édition 2025", "Brochure · 2025 Edition")}
          </h2>
          
          <p className="text-base leading-relaxed text-[#6B5A4D] mb-6">
            {t("Le programme 2025 comprenait :", "The 2025 program included:")}
          </p>
          
          {/* Liste du programme */}
          <ul className="text-sm text-[#5C4A3D] space-y-2 mb-8 max-w-md mx-auto text-left">
            <li>{t("– méditations guidées et pratiques corporelles quotidiennes", "– guided meditations and daily embodied practices")}</li>
            <li>{t("– cérémonies traditionnelles balinaises", "– traditional Balinese ceremonies")}</li>
            <li>{t("– excursions vers cascades sacrées, rizières classées UNESCO et temples", "– excursions to sacred waterfalls, UNESCO-listed rice terraces, and temples")}</li>
            <li>{t("– accès spa et massages balinais", "– spa access and Balinese massages")}</li>
            <li>{t("– cercles d'intégration et accompagnement personnalisé", "– integration circles and personalized support")}</li>
          </ul>

          <a
            href="https://customer-assets.emergentagent.com/job_a9e4b3eb-6cf0-46b7-85a6-000614fbaf25/artifacts/97w9lmxe_Cocon%20Sacre%CC%81%20-%20Mars%202025%20-%20Brochure.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-[#8B7B6B] text-[#F5EDE4] text-sm tracking-[0.15em] uppercase shadow-md hover:bg-[#5C4A3D] transition-colors duration-300"
            data-testid="brochure-bali-2025"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="7 10 12 15 17 10"/>
              <line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
            {t("Télécharger la brochure 2025", "Download the 2025 brochure")}
          </a>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* GALERIE - STRUCTURE PREMIUM */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <section className="py-16 px-0 bg-[#F5EDE4]">
        <div className="max-w-6xl mx-auto">
          
          {/* Gallery Header */}
          <div className="text-center mb-16 px-6">
            <h2 className="text-sm tracking-[0.3em] uppercase font-medium text-[#5C4A3D] mb-8">
              {t("Galerie · Bali 2025", "Gallery · Bali 2025")}
            </h2>
            
            {/* Opening phrase */}
            <div className="max-w-lg mx-auto space-y-2">
              <p className="text-base text-[#5C4A3D] leading-relaxed">
                {t("Quelques fragments d'une retraite vécue.", "Fragments of a retreat as it was truly lived.")}
              </p>
              <p className="text-sm text-[#8B7B6B]">
                {t(<>Sans mise en scène.<br />Sans filtre.</>, <>Unstaged.<br />Unfiltered.</>)}
              </p>
              <p className="text-sm text-[#5C4A3D] italic pt-2">
                {t("Simplement la vie telle qu'elle s'est déployée.", "Simply life unfolding.")}
              </p>
            </div>
          </div>

          {/* Note sur les images */}
          <div className="max-w-xl mx-auto mb-16 px-6 text-center">
            <p className="text-sm text-[#8B7B6B] leading-relaxed">
              {t(
                <>Ces images ne sont pas celles d'une production photographique.<br />Ce sont des instants bruts. Vivants. Imparfaits parfois.<br /><span className="text-[#5C4A3D] font-medium">Mais profondément vrais.</span></>,
                <>These images were not captured as part of a professional production.<br />They are raw moments. Alive. Sometimes imperfect.<br /><span className="text-[#5C4A3D] font-medium">Yet deeply real.</span></>
              )}
            </p>
          </div>

          {/* ═══ 1. GRANDE IMAGE HERO ═══ */}
          <div className="mb-12">
            <img 
              src="https://customer-assets.emergentagent.com/job_c4b2413e-fbb2-49c4-b5be-8ac09232191c/artifacts/enldxzbd_PHOTO-2025-03-15-20-17-55.jpg"
              alt={t("COCON Sacré Bali 2025 - Posture du danseur au Candi Bentar avec reflet parfait", "COCON Sacré Bali 2025 - Dancer pose at Candi Bentar with perfect reflection")}
              className="w-full aspect-[21/9] object-cover object-center"
            />
          </div>

          {/* ═══ 2. SÉRIE 1 — CERCLES ═══ */}
          <div className="mb-16 px-6">
            <p className="text-xs tracking-[0.25em] uppercase text-[#8B7B6B] text-center mb-6">
              {t("Cercles", "Circles")}
            </p>
            <div className="grid grid-cols-3 gap-4">
              <img src="https://customer-assets.emergentagent.com/job_c4b2413e-fbb2-49c4-b5be-8ac09232191c/artifacts/w6ef64kr_PHOTO-2025-03-19-14-36-23.jpg" alt={t("Cercle de méditation dans shala bambou", "Meditation circle in bamboo shala")} className="aspect-[4/3] object-cover rounded-lg" />
              <img src="https://customer-assets.emergentagent.com/job_c4b2413e-fbb2-49c4-b5be-8ac09232191c/artifacts/79rc27x7_PHOTO-2025-03-15-11-51-01.jpg" alt={t("Enfants en cercle coloriant mandala", "Children in circle coloring mandala")} className="aspect-[4/3] object-cover rounded-lg" />
              <img src="https://customer-assets.emergentagent.com/job_c4b2413e-fbb2-49c4-b5be-8ac09232191c/artifacts/f4d2sjjv_PHOTO-2025-03-18-11-12-23.jpg" alt={t("Pratique en cercle bras ouverts", "Circle practice with open arms")} className="aspect-[4/3] object-cover rounded-lg" />
            </div>
          </div>

          {/* ═══ 3. SÉRIE 2 — RITUELS ═══ */}
          <div className="mb-16 px-6">
            <p className="text-xs tracking-[0.25em] uppercase text-[#8B7B6B] text-center mb-6">
              {t("Rituels", "Rituals")}
            </p>
            <div className="grid grid-cols-3 gap-4">
              <img src="https://customer-assets.emergentagent.com/job_c4b2413e-fbb2-49c4-b5be-8ac09232191c/artifacts/0cxz9wjs_PHOTO-2025-03-13-17-52-19.jpg" alt={t("Cérémonie d'eau sacrée avec prêtre balinais", "Sacred water ceremony with Balinese priest")} className="aspect-[4/3] object-cover rounded-lg" />
              <img src="https://customer-assets.emergentagent.com/job_c4b2413e-fbb2-49c4-b5be-8ac09232191c/artifacts/on0g5lx5_PHOTO-2025-03-17-06-54-29.jpg" alt={t("Cérémonie cacao avec coeur central", "Cacao ceremony with heart center")} className="aspect-[4/3] object-cover rounded-lg" />
              <img src="https://customer-assets.emergentagent.com/job_c4b2413e-fbb2-49c4-b5be-8ac09232191c/artifacts/2bu4sazl_PHOTO-2025-03-17-19-37-10%202.jpg" alt={t("Jeinar préparant cérémonie dans shala", "Jeinar preparing ceremony in shala")} className="aspect-[4/3] object-cover object-[center_65%] rounded-lg" />
            </div>
          </div>

          {/* ═══ 4. GRANDE IMAGE RESPIRANTE ═══ */}
          <div className="mb-16">
            <img 
              src="https://customer-assets.emergentagent.com/job_bf09b751-ac59-4e7b-bc7f-ba26a9ddb83c/artifacts/p1egijky_PHOTO-2025-03-13-17-57-43.jpg"
              alt={t("Lumbung traditionnel balinais avec piscine turquoise et jardin tropical", "Traditional Balinese lumbung with turquoise pool and tropical garden")}
              className="w-full aspect-[16/10] object-cover object-[center_70%]"
            />
          </div>

          {/* ═══ 5. SÉRIE 3 — NATURE ═══ */}
          <div className="mb-16 px-6">
            <p className="text-xs tracking-[0.25em] uppercase text-[#8B7B6B] text-center mb-6">
              Nature
            </p>
            <div className="grid grid-cols-3 gap-4">
              <img src="https://customer-assets.emergentagent.com/job_c4b2413e-fbb2-49c4-b5be-8ac09232191c/artifacts/t3o61h1v_PHOTO-2025-03-18-09-57-38.jpg" alt={t("Statue Bouddha dans les rizières d'Ubud", "Buddha statue in Ubud rice fields")} className="aspect-[4/3] object-cover rounded-lg" />
              <img src="https://customer-assets.emergentagent.com/job_c4b2413e-fbb2-49c4-b5be-8ac09232191c/artifacts/zc3a3mf9_PHOTO-2025-03-16-14-53-51.jpg" alt={t("Contemplation face à la cascade sacrée", "Contemplation facing sacred waterfall")} className="aspect-[4/3] object-cover rounded-lg" />
              <img src="https://customer-assets.emergentagent.com/job_c4b2413e-fbb2-49c4-b5be-8ac09232191c/artifacts/g5zjwn7s_PHOTO-2025-03-15-21-06-17.jpg" alt={t("Fleurs de lotus et Bouddha doré", "Lotus flowers and golden Buddha")} className="aspect-[4/3] object-cover object-top rounded-lg" />
            </div>
          </div>

          {/* ═══ 6. BLOC VIDÉO ═══ */}
          <div className="mb-16 px-6">
            <p className="text-xs tracking-[0.25em] uppercase text-[#8B7B6B] text-center mb-6">
              {t("Vidéo · La traversée", "Video · The Journey")}
            </p>
            <div className="max-w-2xl mx-auto rounded-2xl overflow-hidden shadow-lg">
              <video 
                controls 
                className="w-full"
                preload="metadata"
                title={t("COCON Sacré Bali 2025 – Vidéo retraite initiatique femmes Ubud", "COCON Sacré Bali 2025 – Women's initiatory retreat video Ubud")}
                aria-label={t("Vidéo de la traversée COCON Sacré Bali mars 2025", "COCON Sacré Bali March 2025 crossing video")}
              >
                <source 
                  src="https://customer-assets.emergentagent.com/job_bf09b751-ac59-4e7b-bc7f-ba26a9ddb83c/artifacts/o3dgor5f_1078BC18-3CED-4129-8F45-20A2DD7712A1.mov" 
                  type="video/quicktime" 
                />
                <source 
                  src="https://customer-assets.emergentagent.com/job_bf09b751-ac59-4e7b-bc7f-ba26a9ddb83c/artifacts/o3dgor5f_1078BC18-3CED-4129-8F45-20A2DD7712A1.mov" 
                  type="video/mp4" 
                />
                {t("Votre navigateur ne supporte pas la lecture vidéo.", "Your browser does not support video playback.")}
              </video>
            </div>
          </div>

          {/* ═══ 7. SÉRIE 4 — VIE QUOTIDIENNE ═══ */}
          <div className="mb-16 px-6">
            <p className="text-xs tracking-[0.25em] uppercase text-[#8B7B6B] text-center mb-6">
              {t("Vie quotidienne", "Daily life")}
            </p>
            <div className="grid grid-cols-3 gap-4">
              <img src="https://customer-assets.emergentagent.com/job_c4b2413e-fbb2-49c4-b5be-8ac09232191c/artifacts/hf0bjyxm_PHOTO-2025-03-17-13-07-28.jpg" alt={t("Groupe de femmes riant avec sacs COCON", "Group of women laughing with COCON bags")} className="aspect-[4/3] object-cover rounded-lg" />
              <img src="https://customer-assets.emergentagent.com/job_c4b2413e-fbb2-49c4-b5be-8ac09232191c/artifacts/apsfv3v6_PHOTO-2025-03-15-12-24-09.jpg" alt={t("Photo de groupe avec enfants de l'école", "Group photo with school children")} className="aspect-[4/3] object-cover rounded-lg" />
              <img src="https://customer-assets.emergentagent.com/job_bf09b751-ac59-4e7b-bc7f-ba26a9ddb83c/artifacts/0lmgtwki_PHOTO-2025-03-13-19-22-38.jpg" alt={t("Selfie de deux participantes devant la cascade sacrée", "Two participants selfie at sacred waterfall")} className="aspect-[4/3] object-cover rounded-lg" />
            </div>
          </div>

          {/* ═══ 8. SÉRIE 5 — TEMPLES ═══ */}
          <div className="mb-16 px-6">
            <p className="text-xs tracking-[0.25em] uppercase text-[#8B7B6B] text-center mb-6">
              Temples
            </p>
            <div className="grid grid-cols-3 gap-4">
              <img src="https://customer-assets.emergentagent.com/job_c4b2413e-fbb2-49c4-b5be-8ac09232191c/artifacts/saw760ck_PHOTO-2025-03-15-21-17-05.jpg" alt={t("Temple Brahmavihara Arama, stupas et brume", "Brahmavihara Arama temple, stupas and mist")} className="aspect-[4/3] object-cover rounded-lg" />
              <img src="https://customer-assets.emergentagent.com/job_c4b2413e-fbb2-49c4-b5be-8ac09232191c/artifacts/refe8d81_PHOTO-2025-03-13-17-57-51.jpg" alt={t("Danseuses balinaises en costume doré", "Balinese dancers in golden costume")} className="aspect-[4/3] object-cover rounded-lg" />
              <img src="https://customer-assets.emergentagent.com/job_c4b2413e-fbb2-49c4-b5be-8ac09232191c/artifacts/kp9likyg_PHOTO-2025-03-15-21-25-28.jpg" alt={t("Prière devant Bouddha doré, rayons de lumière", "Prayer before golden Buddha, light rays")} className="aspect-[4/3] object-cover rounded-lg" />
            </div>
          </div>

          {/* ═══ 9. SÉRIE 6 — OFFRANDES ═══ */}
          <div className="mb-16 px-6">
            <p className="text-xs tracking-[0.25em] uppercase text-[#8B7B6B] text-center mb-6">
              {t("Offrandes", "Offerings")}
            </p>
            <div className="grid grid-cols-3 gap-4">
              <img src="https://customer-assets.emergentagent.com/job_c4b2413e-fbb2-49c4-b5be-8ac09232191c/artifacts/jtcq2nfe_PHOTO-2025-03-15-07-00-16%202.jpg" alt={t("Mandala de fleurs multicolores", "Multicolored flower mandala")} className="aspect-[4/3] object-cover rounded-lg" />
              <img src="https://customer-assets.emergentagent.com/job_c4b2413e-fbb2-49c4-b5be-8ac09232191c/artifacts/gbib9ok8_PHOTO-2025-03-21-18-38-52.jpg" alt={t("Participantes avec offrandes canang sari", "Participants with canang sari offerings")} className="aspect-[4/3] object-cover rounded-lg" />
              <img src="https://customer-assets.emergentagent.com/job_c4b2413e-fbb2-49c4-b5be-8ac09232191c/artifacts/d8phxhac_PHOTO-2025-03-16-14-53-34.jpg" alt={t("Offrande naturelle près de la cascade", "Natural offering near waterfall")} className="aspect-[4/3] object-cover rounded-lg" />
            </div>
          </div>

          {/* ═══ 10. SÉRIE 7 — SILENCES ═══ */}
          <div className="mb-16 px-6">
            <p className="text-xs tracking-[0.25em] uppercase text-[#8B7B6B] text-center mb-6">
              {t("Silences", "Silences")}
            </p>
            <div className="grid grid-cols-3 gap-4">
              <img src="https://customer-assets.emergentagent.com/job_c4b2413e-fbb2-49c4-b5be-8ac09232191c/artifacts/04vvvkqh_PHOTO-2025-03-15-07-00-14%202.jpg" alt={t("Méditation face au Bouddha dans le jardin", "Meditation facing Buddha in garden")} className="aspect-[4/3] object-cover rounded-lg" />
              <img src="https://customer-assets.emergentagent.com/job_c4b2413e-fbb2-49c4-b5be-8ac09232191c/artifacts/onqaat69_PHOTO-2025-03-15-21-23-03%202.jpg" alt={t("Contemplation à la porte du temple, robe rouge", "Temple gate contemplation, red dress")} className="aspect-[4/3] object-cover object-[center_10%] rounded-lg" />
              <img src="https://customer-assets.emergentagent.com/job_bf09b751-ac59-4e7b-bc7f-ba26a9ddb83c/artifacts/bh60y9io_PHOTO-2025-03-18-16-57-19.jpg" alt={t("Femme buvant dans une tasse lors de la cérémonie", "Woman drinking from cup during ceremony")} className="aspect-[4/3] object-cover object-[center_38%] rounded-lg" />
            </div>
          </div>

          {/* ═══ 11. SÉRIE 8 — LUMIÈRES ═══ */}
          <div className="mb-12 px-6">
            <p className="text-xs tracking-[0.25em] uppercase text-[#8B7B6B] text-center mb-6">
              {t("Lumières", "Lights")}
            </p>
            <div className="grid grid-cols-3 gap-4">
              <img src="https://customer-assets.emergentagent.com/job_c4b2413e-fbb2-49c4-b5be-8ac09232191c/artifacts/xg83cmcc_PHOTO-2025-03-15-07-00-13.jpg" alt={t("Shala pyramidale bambou illuminée la nuit", "Pyramid bamboo shala illuminated at night")} className="aspect-[4/3] object-cover rounded-lg" />
              <img src="https://customer-assets.emergentagent.com/job_c4b2413e-fbb2-49c4-b5be-8ac09232191c/artifacts/n8htsv67_PHOTO-2025-03-14-00-43-44.jpg" alt={t("Cérémonie d'accueil avec mandala et staff", "Welcome ceremony with mandala and staff")} className="aspect-[4/3] object-cover rounded-lg" />
              <img src="https://customer-assets.emergentagent.com/job_c4b2413e-fbb2-49c4-b5be-8ac09232191c/artifacts/l2tjdmmt_PHOTO-2025-03-13-17-57-42.jpg" alt={t("Shala bambou de jour, ciel bleu", "Bamboo shala by day, blue sky")} className="aspect-[4/3] object-cover rounded-lg" />
            </div>
          </div>

          {/* Gallery Closing */}
          <div className="text-center pt-8 pb-4 px-6">
            <div className="w-16 h-px bg-[#8B7B6B]/30 mx-auto mb-8" />
            <p className="text-sm text-[#5C4A3D] italic leading-relaxed mb-4">
              {t(
                <>Ces images ne cherchent pas à montrer une retraite parfaite.<br />Elles témoignent simplement de ce qui s'est réellement vécu.</>,
                <>These images do not attempt to portray a perfect retreat.<br />They simply reveal what was truly lived.</>
              )}
            </p>
            <p className="text-sm text-[#8B7B6B] italic leading-relaxed">
              {t(
                "Elles ouvrent une fenêtre sur l'intimité de cette première retraite.",
                "They offer a glimpse into the intimacy of this first retreat."
              )}
            </p>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* PRE-COCON */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <section className="py-12 px-6 bg-[#E8DED4]">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-sm tracking-[0.3em] uppercase font-medium text-[#5C4A3D] mb-6">
            {t("Préparation avant la retraite · PRÉ-COCON", "Preparation Before the Retreat · PRE-COCOON")}
          </h2>
          <p className="text-base leading-relaxed text-[#6B5A4D] mb-6">
            {t("Chaque participante bénéficiait d'un accompagnement préparatoire.", "Each participant received preparatory support before the retreat.")}
          </p>
          
          <p className="text-sm text-[#5C4A3D] mb-2">{t("Objectif :", "Objectives:")}</p>
          <ul className="text-sm text-[#6B5A4D] space-y-1 mb-10">
            <li>{t("– clarifier l'intention", "– to clarify intention")}</li>
            <li>{t("– ouvrir l'espace intérieur", "– to open the inner space")}</li>
            <li>{t("– entrer dans la traversée avec conscience", "– to enter the journey with awareness")}</li>
          </ul>

          {/* Vidéo 1er Fil d'Or */}
          <div className="max-w-2xl mx-auto">
            <p className="text-xs tracking-[0.15em] uppercase text-[#8B7B6B] mb-4">
              {t("Première communication aux Coconées", "First communication to the Coconées")}
            </p>
            <div className="rounded-xl overflow-hidden shadow-lg">
              <video 
                controls 
                className="w-full"
                preload="metadata"
              >
                <source 
                  src="https://customer-assets.emergentagent.com/job_b2472d9f-7229-4306-b190-d947a8bab60d/artifacts/tl5do8gd_1er%20Fil%20d%E2%80%99Or.mov" 
                  type="video/quicktime" 
                />
                <source 
                  src="https://customer-assets.emergentagent.com/job_b2472d9f-7229-4306-b190-d947a8bab60d/artifacts/tl5do8gd_1er%20Fil%20d%E2%80%99Or.mov" 
                  type="video/mp4" 
                />
              </video>
            </div>
          </div>

          {/* Mail du 02.02.25 - Accordéon */}
          <div className="max-w-2xl mx-auto mt-10 bg-[#F5EDE4] border border-[#8B7B6B]/20 rounded-2xl p-6 shadow-sm text-left">
            <p className="text-xs tracking-[0.2em] uppercase text-[#8B7B6B] text-center mb-4">
              {t("3ème Fil d'Or · 02.02.2025", "3rd Golden Thread · 02.02.2025")}
            </p>

            {/* Aperçu visible */}
            <div className="space-y-4 text-sm leading-relaxed text-[#6B5A4D]">
              <p>{t("Chère et douce Coconée Sacrée,", "Dear and gentle Sacred Coconée,")}</p>
              
              <p>
                {t(
                  "Comme un rituel sacré, je t'invite à te connecter à la playlist Cocon Sacré et à aller directement à la chanson n°19. Laisse cette mélodie t'immerger dans les belles énergies qui t'attendent.",
                  "Like a sacred ritual, I invite you to connect with the COCON Sacré playlist and go directly to song number 19. Let this melody immerse you in the beautiful energies that await you."
                )}
              </p>
              
              <p>
                {t(
                  "Oui, prends un instant pour toi. Respire profondément, sens l'air circuler en toi...",
                  "Yes, take a moment for yourself. Breathe deeply. Feel the air moving through you…"
                )}
              </p>
            </div>

            {/* Contenu déroulable */}
            <div className={`overflow-hidden transition-all duration-500 ease-in-out ${isPreCoconExpanded ? 'max-h-[5000px] opacity-100 mt-4' : 'max-h-0 opacity-0'}`}>
              <div className="space-y-5 text-sm leading-relaxed text-[#6B5A4D]">
                <p>
                  ...ressens la vie vibrer dans chaque cellule de ton être.
                </p>
                
                <p>
                  Nous sommes le 2.2 2025, un jour qui marque le premier pas dans un nouveau mois, mais aussi une transition énergétique sous l'influence du serpent de bois, gardien de l'année chinoise qui vient d'émerger.
                </p>

                {/* Nouvelle Lune */}
                <div className="border-t border-[#8B7B6B]/20 pt-5">
                  <p className="font-medium text-[#5C4A3D] mb-4">Une Nouvelle Lune pour s'élever et s'aligner</p>
                  <p className="mb-4">
                    Nous avons toutes reçu une énergie puissante et vibrante, celle de la première Nouvelle Lune de l'année, en Verseau.
                  </p>
                  <p className="mb-4">
                    💫 Cette lunaison est une invitation à l'audace, à l'ouverture et à l'élévation. Elle nous pousse à élargir notre vision du futur, à nous affranchir des limitations passées et à nous connecter à ce qui nous fait vibrer profondément.
                  </p>
                </div>

                {/* S'ancrer */}
                <div className="border-t border-[#8B7B6B]/20 pt-5">
                  <p className="font-medium text-[#5C4A3D] mb-4">💡 S'ancrer et accueillir cette énergie avec douceur et puissance</p>
                  <p className="mb-4">
                    Je t'invite à prendre un moment rien que pour toi, dans un espace chaleureux et sécurisant, et à déposer ton intention pour ce mois de février.
                  </p>
                  
                  <p className="mb-2">🌿 Pose-toi ces questions avec bienveillance et curiosité :</p>
                  <ul className="space-y-1 mb-4 text-[#5C4A3D]">
                    <li>• Qu'est-ce que je ressens au plus profond de moi en ce moment ?</li>
                    <li>• Quels nouveaux chemins suis-je prête à explorer ?</li>
                    <li>• Quels poids puis-je laisser derrière moi pour avancer avec plus de légèreté et de joie ?</li>
                    <li>• Comment puis-je inviter plus d'harmonie et de fluidité dans mon quotidien ?</li>
                  </ul>
                </div>

                {/* Rituel */}
                <div className="bg-[#E8DED4]/50 rounded-xl p-5">
                  <p className="font-medium text-[#5C4A3D] mb-4">🌙 Petit rituel énergisant et apaisant</p>
                  <p className="mb-4">
                    Installe-toi confortablement, ferme les yeux, et prends trois grandes respirations profondes.
                  </p>
                  <p className="mb-4">
                    Pose une main sur ton cœur et une autre sur ton ventre, et répète ces mots avec douceur :
                  </p>
                  <p className="italic text-[#5C4A3D] text-center mb-4">
                    "Je me libère avec fluidité et confiance de ce qui ne me sert plus. J'accueille avec sérénité et gratitude les belles opportunités qui s'ouvrent à moi. Chaque jour, je choisis d'illuminer mon quotidien avec amour et énergie."
                  </p>
                  <p className="text-[#8B7B6B] italic">
                    Sens cette vibration douce et chaude se diffuser dans tout ton corps, t'envelopper comme une étreinte réconfortante.
                  </p>
                </div>

                {/* Fil d'or */}
                <div className="border-t border-[#8B7B6B]/20 pt-5">
                  <p className="font-medium text-[#5C4A3D] mb-4">Un fil d'or pour aller plus loin, ensemble</p>
                  <p className="mb-4">
                    Depuis le 10 janvier, nous tissons semaine après semaine un cocon de lumière et de présence. Aujourd'hui, j'ai le plaisir de te faire parvenir le 3e Fil d'Or de Cocon Sacré.
                  </p>
                  <p className="mb-4">
                    Avec ce 3e Fil d'Or, tu vas découvrir plus en détail les Coconeuses Sacrées.
                  </p>
                </div>

                {/* Permission */}
                <div className="border-t border-[#8B7B6B]/20 pt-5">
                  <p className="font-medium text-[#5C4A3D] mb-4">Une permission d'évoluer</p>
                  <p className="mb-4">
                    Alors aujourd'hui, je t'invite à choisir. À t'autoriser à muer. À te donner la permission d'avancer, avec douceur, dans ce cocon que nous tissons ensemble.
                  </p>
                  <p className="italic text-[#8B7B6B]">
                    Nous sommes là, à chaque instant, pour t'accompagner, pour accueillir tes mots, tes ressentis, tes élans. Ce chemin est le tien, et nous sommes honorées de le parcourir avec toi.
                  </p>
                </div>

                {/* Signature */}
                <div className="border-t border-[#8B7B6B]/20 pt-5 mt-5 text-center">
                  <p className="italic text-[#8B7B6B] mb-4">
                    Que cette semaine t'enveloppe d'une lumière douce et profonde, comme un souffle sacré venant nourrir ton être.
                  </p>
                  <p className="font-medium text-[#5C4A3D] mb-4">
                    Je t'aime.
                  </p>
                  <p className="text-[#8B7B6B]">
                    Avec toute ma lumière et celle des Coconeuses Sacrées,
                  </p>
                  <p className="mt-2 text-[#5C4A3D] font-medium">Jeinar</p>
                </div>
              </div>
            </div>

            {/* Bouton déroulant */}
            <button
              onClick={() => setIsPreCoconExpanded(!isPreCoconExpanded)}
              className="w-full mt-4 flex items-center justify-center gap-2 py-3 text-sm text-[#8B7B6B] hover:text-[#5C4A3D] transition-colors"
            >
              <span>{isPreCoconExpanded ? t('Réduire', 'Read less') : t('Lire la suite', 'Read more')}</span>
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                width="16" 
                height="16" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
                className={`transition-transform duration-300 ${isPreCoconExpanded ? 'rotate-180' : ''}`}
              >
                <polyline points="6 9 12 15 18 9"/>
              </svg>
            </button>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* POST-COCON */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <section className="py-12 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-sm tracking-[0.3em] uppercase font-medium text-[#5C4A3D] mb-6">
            {t("Intégration après la retraite · POST-COCON", "Integration After the Retreat · POST-COCOON")}
          </h2>
          <p className="text-base leading-relaxed text-[#6B5A4D] mb-4">
            {t("La transformation ne s'arrête pas au retour.", "The transformation does not end when you return home.")}
          </p>
          <p className="text-sm text-[#8B7B6B] mb-10">
            {t(
              "Un accompagnement post-retraite soutenait l'ancrage des prises de conscience et l'intégration dans la vie quotidienne.",
              "Post-retreat support helped anchor each realization and integrate the experience into daily life."
            )}
          </p>

          {/* Premier mail POST-COCON - Accordéon */}
          <div className="bg-[#F5EDE4] border border-[#8B7B6B]/20 rounded-2xl p-6 md:p-8 shadow-sm text-left">
            <p className="text-xs tracking-[0.2em] uppercase text-[#8B7B6B] text-center mb-6">
              {t("Premier message post-retraite", "First post-retreat message")}
            </p>
            
            <h3 className="text-base font-medium text-[#5C4A3D] text-center mb-6 italic">
              {t("Pleine Lune en Balance : Le Retour au Cocon", "Full Moon in Libra: Returning to the Cocoon")}
            </h3>

            {/* Aperçu visible */}
            <div className="space-y-4 text-sm leading-relaxed text-[#6B5A4D]">
              <p>{t("Mes chères Coconées Sacrées,", "My dear Sacred Coconées,")}</p>
              
              <p>
                {t(
                  "Il y a un mois, nous achevions ensemble une traversée unique, portée par l'énergie de Bali, la puissance de la sororité, et la magie du sacré.",
                  "One month ago, we completed together a unique journey carried by the energy of Bali, the power of sisterhood, and the magic of the sacred."
                )}
              </p>
              
              <p>
                {t(
                  <>Ce soir, la lune pleine dans le signe de la Balance vient éclairer ce fil invisible qui nous relie encore.
                C'est une <span className="font-medium italic">Trame de Soie</span>...</>,
                  <>Tonight, the full moon in Libra comes to illuminate the invisible thread that still connects us. It is a <span className="font-medium italic">Silk Weave</span>…</>
                )}
              </p>
            </div>

            {/* Contenu déroulable */}
            <div className={`overflow-hidden transition-all duration-500 ease-in-out ${isPostCoconExpanded ? 'max-h-[3000px] opacity-100 mt-4' : 'max-h-0 opacity-0'}`}>
              <div className="space-y-5 text-sm leading-relaxed text-[#6B5A4D]">
                <p>
                  ...douce, souple, invisible parfois… mais indestructible.
                </p>
                
                <p>
                  J'avais souhaité laisser passer 30 jours — 30 jours pour atterrir, déposer, intégrer.<br />
                  Aujourd'hui, je suis heureuse de reprendre ce fil qui nous relie.
                </p>

                <p className="text-center text-[#5C4A3D]">
                  ✨ Et pour ce redémarrage tout en douceur, que diriez-vous… de commencer par vous reconnecter ?
                </p>

                <p className="text-center text-[#8B7B6B] italic">
                  À vous-même. Au groupe. À Cocon. À Bali.
                </p>

                {/* Pleine lune */}
                <div className="border-t border-[#8B7B6B]/20 pt-5 mt-5">
                  <p className="text-center font-medium text-[#5C4A3D] mb-4">🌕 Une pleine lune, un rappel d'équilibre</p>
                  <p>
                    Cette pleine lune éclaire nos liens. Elle révèle les "oui" à contre-cœur, les silences lourds, les déséquilibres subtils.
                  </p>
                  <p className="text-center italic my-4 text-[#5C4A3D]">
                    « Et si je n'avais plus à choisir entre être aimée et être pleinement fidèle à moi-même ? »
                  </p>
                </div>

                {/* Questions */}
                <div className="bg-[#E8DED4]/50 rounded-xl p-5">
                  <p className="mb-3">Questions pour vous guider :</p>
                  <ul className="space-y-2 text-[#5C4A3D]">
                    <li>• Où est-ce que mon corps dit "non", même si je continue de dire "oui" ?</li>
                    <li>• Qu'ai-je besoin de rééquilibrer dans ma façon d'aimer, de recevoir, d'exprimer ?</li>
                    <li>• Quelle part de moi a besoin de revenir au centre ?</li>
                  </ul>
                </div>

                {/* Signature */}
                <div className="border-t border-[#8B7B6B]/20 pt-5 mt-5 text-center">
                  <p className="font-medium text-[#5C4A3D] mb-4">
                    L'équilibre ne se trouve pas. Il se crée. En vous.
                  </p>
                  <p className="text-[#8B7B6B]">
                    Avec toute ma lumière,<br />
                    et la délicatesse de cette toute première Trame de Soie
                  </p>
                  <p className="mt-3 text-[#5C4A3D] font-medium">💫 Jeinar</p>
                </div>
              </div>
            </div>

            {/* Bouton déroulant */}
            <button
              onClick={() => setIsPostCoconExpanded(!isPostCoconExpanded)}
              className="w-full mt-6 flex items-center justify-center gap-2 py-3 text-sm text-[#8B7B6B] hover:text-[#5C4A3D] transition-colors"
            >
              <span>{isPostCoconExpanded ? t('Réduire', 'Read less') : t('Lire la suite', 'Read more')}</span>
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                width="16" 
                height="16" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
                className={`transition-transform duration-300 ${isPostCoconExpanded ? 'rotate-180' : ''}`}
              >
                <polyline points="6 9 12 15 18 9"/>
              </svg>
            </button>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* ET 2026 ? */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <section className="py-10 px-6">
        <div className="max-w-3xl mx-auto text-center space-y-6 bg-[#E8D2B5]/60 rounded-2xl px-6 py-8 text-[#3F2A24]">
          <h2 className="text-xs md:text-sm tracking-[0.35em] uppercase font-medium text-[#3F2A24]">
            {t("Et 2026 ?", "And in 2026?")}
          </h2>
          
          <p className="text-base leading-relaxed">
            {t("L'édition 2025 a été une fondation.", "The 2025 edition was a foundation.")}
          </p>
          
          <p className="text-base leading-relaxed">
            {t(
              <>En 2026, <span className="font-medium uppercase">COCON Sacré</span> évolue.</>,
              <>In 2026, <span className="font-medium uppercase">COCON Sacré</span> evolves.</>
            )}
          </p>
          
          <p className="text-sm">
            {t(
              <>La traversée devient un arc structuré à travers plusieurs territoires : <span className="font-medium">Gili, Bali, Java</span>.</>,
              <>The journey becomes a structured arc across several territories: <span className="font-medium">Gili · Bali · Java</span>.</>
            )}
          </p>
          
          {/* Bloc distinctif */}
          <div className="border-l-2 border-[#3F2A24]/30 pl-5 py-2 text-left max-w-sm mx-auto">
            <p className="text-sm italic">
              {t(
                <>Un format plus ample.<br />Une progression plus précise.<br />Une immersion plus profonde.</>,
                <>A broader format.<br />A more precise progression.<br />A deeper immersion.</>
              )}
            </p>
          </div>

          <p className="text-sm opacity-80">
            {t("Si vous souhaitez découvrir l'expérience 2026 :", "If you would like to discover the 2026 experience:")}
          </p>

          <Link
            to="/cocon-sacre-2026"
            className="inline-flex items-center justify-center gap-2 px-8 py-3 rounded-full bg-[#3F2A24] text-[#F3E5CF] text-sm tracking-[0.15em] uppercase shadow-md hover:bg-[#6E3A1E] transition-colors duration-300"
            data-testid="cta-bali-2026"
          >
            {t("Découvrir COCON Sacré 2026", "Discover COCON Sacré 2026")}
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="5" y1="12" x2="19" y2="12"/>
              <polyline points="12 5 19 12 12 19"/>
            </svg>
          </Link>
        </div>
      </section>

      {/* Footer navigation */}
      <section className="py-8 px-6">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <a
            href="/cocon-sacre-2026"
            className="text-sm text-[#8B7B6B] hover:text-[#5C4A3D] transition-colors"
          >
            {t("← Retour à l'accueil", "← Back to home")}
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Cocons2025Bali;
